import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

interface MaterializeResultViewProps {
  result: StudioMaterializeResult;
  documentPath: string | null;
  searchParamsString: string;
  onBackToTemplates: () => void;
  onViewDocument: () => void;
  onRetry: () => void;
}

interface EntityGroupProps {
  label: string;
  entities: StudioMaterializedEntity[];
  buildLink: (key: string) => string;
}

function EntityRow({
  entity,
  link,
}: {
  entity: StudioMaterializedEntity;
  link: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        paddingX: 1.5,
        paddingY: 1,
        border: "1px solid #EEE",
        borderRadius: 1,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {entity.label}
        </Typography>
        {entity.sublabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {entity.sublabel}
          </Typography>
        )}
      </Box>
      <Button
        component={Link}
        to={link}
        size="small"
        variant="text"
        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        sx={{ flexShrink: 0 }}
      >
        Open
      </Button>
    </Stack>
  );
}

function EntityGroup({ label, entities, buildLink }: EntityGroupProps) {
  if (entities.length === 0) return null;
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label} ({entities.length})
      </Typography>
      <Stack spacing={0.75} sx={{ marginTop: 0.5 }}>
        {entities.map((e) => (
          <EntityRow key={e.key} entity={e} link={buildLink(e.key)} />
        ))}
      </Stack>
    </Box>
  );
}

export default function MaterializeResultView({
  result,
  documentPath,
  searchParamsString,
  onBackToTemplates,
  onViewDocument,
  onRetry,
}: MaterializeResultViewProps) {
  const paymentEntities = result.payment ? [result.payment] : [];
  const createdCounts: string[] = [];
  if (paymentEntities.length) createdCounts.push("1 payment");
  if (result.patients.length)
    createdCounts.push(
      `${result.patients.length} patient${result.patients.length === 1 ? "" : "s"}`,
    );
  if (result.claims.length)
    createdCounts.push(
      `${result.claims.length} claim${result.claims.length === 1 ? "" : "s"}`,
    );

  const anyCreated =
    paymentEntities.length + result.patients.length + result.claims.length > 0;
  const errorCount = result.errors.length;

  let headerIcon = (
    <InfoOutlinedIcon sx={{ color: "info.main", fontSize: 28 }} />
  );
  let headerTitle = "Nothing was created";
  let headerSubtitle =
    "No entities resolved from the bindings. Review the document and bindings, then try again.";
  if (anyCreated && errorCount === 0) {
    headerIcon = (
      <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
    );
    headerTitle = "Entities created";
    headerSubtitle = "All resolved entities were created successfully.";
  } else if (anyCreated && errorCount > 0) {
    headerIcon = (
      <WarningAmberIcon sx={{ color: "warning.main", fontSize: 28 }} />
    );
    headerTitle = `Created with ${errorCount} issue${errorCount === 1 ? "" : "s"}`;
    headerSubtitle =
      "Some entities were created. Review the issues below, adjust bindings, and retry.";
  } else if (!anyCreated && errorCount > 0) {
    headerIcon = (
      <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 28 }} />
    );
    headerTitle = "Materialization failed";
    headerSubtitle =
      "No entities were created. Review the issues below, adjust bindings, and retry.";
  }

  const qs = searchParamsString ? `?${searchParamsString}` : "";

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          padding: { xs: 2, md: 4 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 720,
            padding: { xs: 2.5, md: 3.5 },
            borderRadius: 2,
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {headerIcon}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {headerTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {headerSubtitle}
                </Typography>
                {createdCounts.length > 0 && (
                  <Stack
                    direction="row"
                    sx={{
                      marginTop: 1.25,
                      flexWrap: "wrap",
                      gap: 0.75,
                    }}
                  >
                    {createdCounts.map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        size="small"
                        sx={{ backgroundColor: "#EDE7FE" }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>

            {anyCreated && (
              <>
                <Divider />
                <Stack spacing={2}>
                  <EntityGroup
                    label="Payment"
                    entities={paymentEntities}
                    buildLink={(k) => `/payments/${k}${qs}`}
                  />
                  <EntityGroup
                    label="Patients"
                    entities={result.patients}
                    buildLink={(k) => `/patients/${k}${qs}`}
                  />
                  <EntityGroup
                    label="Claims"
                    entities={result.claims}
                    buildLink={(k) => `/claims/${k}${qs}`}
                  />
                </Stack>
              </>
            )}

            {errorCount > 0 && (
              <>
                <Divider />
                <Stack spacing={1.5}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Issues ({errorCount})
                  </Typography>
                  <Stack spacing={1}>
                    {result.errors.map((err) => (
                      <Stack
                        key={`${err.entity}-${err.index ?? "x"}-${err.message}`}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        sx={{
                          padding: 1.25,
                          border: "1px solid",
                          borderColor: "warning.light",
                          borderRadius: 1,
                          backgroundColor: "#FFF8E1",
                        }}
                      >
                        <WarningAmberIcon
                          sx={{
                            color: "warning.main",
                            fontSize: 18,
                            marginTop: 0.25,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {err.entity}
                            {err.index !== null ? ` #${err.index}` : ""}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {err.message}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        sx={{
          paddingX: 3,
          paddingY: 2,
          borderTop: "1px solid #E0E0E0",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Button
          variant="outlined"
          size="medium"
          disabled={!documentPath}
          onClick={onViewDocument}
          startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
        >
          View source document
        </Button>
        <Button
          variant={errorCount > 0 ? "outlined" : "contained"}
          size="medium"
          onClick={onBackToTemplates}
        >
          Back to templates
        </Button>
        {errorCount > 0 && (
          <Button
            variant="contained"
            size="medium"
            onClick={onRetry}
            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
          >
            Retry
          </Button>
        )}
      </Stack>
    </Box>
  );
}
